import metaModel from '~/shared/src/utilities/metaModel';
import User from '~/shared/src/model/User';
import {
    buildDataDescriptor,
    DataDescriptor,
    DataObjectReferenceProperties,
    DataStructure,
    ObjectDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {ColumnDefinition} from '~/shared/src/validationSchemes/modules/overview';
import {
    getDataStructure as getFinderDataStructure,
    getItemAttributeColumnDefinitions,
    getReferenceColumnDefinitions,
    getPropertyColumnDefinitions,
    getEnumPropertyColumnDefinitions,
    getTargetArea,
    convertItemTypeId
} from '~/shared/src/utilities/modules/finder';

/**
 * Builds the dataStructure
 *
 * @param {String} schema
 * @param {String} itemTypeName
 * @param {String} parentItemTypeName
 * @param {User} user
 *
 * @return {DataStructure}
 */
export const getDataStructure = ( schema : Schema, itemTypeName : string, parentItemTypeName : string, user : User ) : DataStructure => {
    const retVal : DataStructure = {
              id    : '0',
              origId: 0,
              item  : {
                  id  : 0,
                  key : 'noItemType',
                  name: 'noItemType'
              }
          },
          itemTypeId             = metaModel[ schema ].getItemType( itemTypeName )?.id,
          parentItemTypeId       = metaModel[ schema ].getItemType( parentItemTypeName )?.id;

    if ( itemTypeId && parentItemTypeId ) {
        const dataStructure                                                                      = getFinderDataStructure( schema, itemTypeName, user ),
              referenceConfiguration : DataObjectReferenceProperties[ 'referenceConfiguration' ] = {};

        retVal.id = String( parentItemTypeId );
        retVal.origId = parentItemTypeId;
        retVal.item.id = parentItemTypeId;
        retVal.item.key = parentItemTypeName;
        retVal.item.name = parentItemTypeName;
        retVal.item.objectSchema = schema;
        retVal.item.objectType = parentItemTypeName;

        referenceConfiguration[ '*' ] = {
            referenceIsMandatory: true
        };

        retVal.dataStructure = [
            {
                id       : String( convertItemTypeId( itemTypeId ) ),
                origId   : convertItemTypeId( itemTypeId ),
                item     : {
                    id          : convertItemTypeId( itemTypeId ),
                    key         : `ref_${itemTypeName}`,
                    name        : itemTypeName,
                    objectSchema: schema,
                    objectType  : itemTypeName
                },
                reference: {
                    id: itemTypeId,
                    referenceConfiguration
                }
            }
        ];

        if ( dataStructure.dataStructure ) {
            retVal.dataStructure[ 0 ].dataStructure = dataStructure.dataStructure;
        }
    }

    return retVal;
}

/**
 * Builds the columnDefinitions for reference search
 *
 * @param {DataObjectProperties} dataStructure
 * @param {String} schema
 * @param {Status} status
 * @param {String} timestamp
 * @param {String} itemTypeName
 * @param {User} user
 *
 * @return {ColumnDefinition[]}
 */
export const getColumnDefinitions = ( dataStructure : DataStructure, schema : Schema, status : Status, timestamp : string, itemTypeName : string, user : User ) : ColumnDefinition[] => {
    let retVal : ColumnDefinition[] = [];

    if ( dataStructure.dataStructure?.length === 1 ) {
        const dataObject = dataStructure.dataStructure[ 0 ];

        // parent reference columnDefinitions
        retVal = retVal.concat( getParentReferenceAttributeColumnDefinitions( dataObject ) );

        // item attribute columnDefinitions
        retVal = retVal.concat( getItemAttributeColumnDefinitions( dataObject ) );

        // reference columnDefinitions
        retVal = retVal.concat( getReferenceColumnDefinitions( dataObject, schema, itemTypeName, user ) );

        // property columnDefinitions
        retVal = retVal.concat( getPropertyColumnDefinitions( dataObject, schema, itemTypeName, user ) );

        // enum property columnDefinitions
        retVal = retVal.concat( getEnumPropertyColumnDefinitions( dataObject, schema, itemTypeName, user ) );

        // metaData columnDefinitions
        retVal = retVal.concat( getParentReferenceMetaDataColumnDefinitions( dataObject, status, timestamp ) );
    }

    return retVal;
}

/**
 * Creates the column definitions for parent reference attributes
 *
 * @param {DataStructure} dataStructure
 *
 * @return {ColumnDefinition[]}
 *
 */
export const getParentReferenceAttributeColumnDefinitions = ( dataStructure : DataStructure ) : ColumnDefinition[] => {
    const retVal : ColumnDefinition[] = [];

    let columnDataDescriptor : DataDescriptor;

    // id
    columnDataDescriptor = buildDataDescriptor( { attributePath: 'reference.id' }, dataStructure ) as ObjectDataDescriptor;
    retVal.push( {
        key           : columnDataDescriptor.alias,
        field         : columnDataDescriptor.alias,
        header        : 'YN.base.finder.reference.idLabel',
        dataDescriptor: columnDataDescriptor,
        permissions   : {},
        editor        : false,
        filter        : true,
        reorderable   : false,
        hidable       : false,
        resizeable    : false,
        width         : 100
    } );

    // referenceTypeName
    //columnDataDescriptor = buildDataDescriptor( { attributePath: 'reference.referenceTypeName' }, dataStructure ) as ObjectDataDescriptor;
    columnDataDescriptor = buildDataDescriptor( { attributePath: 'reference.referenceTypeNameWithDirection' }, dataStructure ) as ObjectDataDescriptor;
    retVal.push( {
        key           : columnDataDescriptor.alias,
        field         : columnDataDescriptor.alias,
        header        : 'YN.base.finder.reference.typeLabel',
        dataDescriptor: columnDataDescriptor,
        permissions   : {},
        editor        : false,
        filter        : false,
        sortable      : false,
        width         : 200,
        minWidth      : 100
    } );

    // direction
    columnDataDescriptor = buildDataDescriptor( { attributePath: 'reference.referenceEnd' }, dataStructure ) as ObjectDataDescriptor;
    retVal.push( {
        key           : columnDataDescriptor.alias,
        field         : columnDataDescriptor.alias,
        header        : 'YN.base.finder.reference.directionLabel',
        dataDescriptor: columnDataDescriptor,
        permissions   : {},
        editor        : false,
        filter        : false,
        sortable      : false,
        width         : 150,
        minWidth      : 100
    } );

    return retVal;
}

/**
 * Creates the column definitions for meta data
 *
 * @param {DataStructure} dataStructure
 * @param {Status} status
 * @param {String} timestamp
 *
 * @return {ColumnDefinition[]}
 */
export const getParentReferenceMetaDataColumnDefinitions = ( dataStructure : DataStructure, status : Status, timestamp : string ) : ColumnDefinition[] => {
    const retVal : ColumnDefinition[] = [],
          targetArea                  = getTargetArea( status, timestamp );

    let columnDataDescriptor : DataDescriptor;

    // status
    columnDataDescriptor = buildDataDescriptor( { attributePath: 'reference.statusId' }, dataStructure ) as ObjectDataDescriptor;
    retVal.push( {
        key           : columnDataDescriptor.alias,
        field         : columnDataDescriptor.alias,
        header        : 'YN.base.finder.status',
        dataDescriptor: columnDataDescriptor,
        permissions   : {},
        editor        : true,
        filter        : true,
        width         : 110
    } );

    // reference meta data columns
    if ( targetArea === 'all' ) {

        // user
        columnDataDescriptor = buildDataDescriptor( { attributePath: 'reference.modifierUserId' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.modifiedBy',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : false,
            filter        : true,
            width         : 128
        } );

        // valid from
        columnDataDescriptor = buildDataDescriptor( { attributePath: 'reference.validFromTime' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.validFrom',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : false,
            filter        : true,
            width         : 128
        } );

        // valid to
        columnDataDescriptor = buildDataDescriptor( { attributePath: 'reference.validToTime' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.validTo',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : false,
            filter        : true,
            width         : 128
        } );
    }

    else {

        // modification
        columnDataDescriptor = buildDataDescriptor( { attributePath: 'reference.modificationTime' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.modifiedAt',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : true,
            filter        : true,
            width         : 128
        } );

        columnDataDescriptor = buildDataDescriptor( { attributePath: 'reference.modifierUserId' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.modifiedBy',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : true,
            filter        : true,
            width         : 128
        } );

        // validation
        columnDataDescriptor = buildDataDescriptor( { attributePath: 'reference.validationTime' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.validatedAt',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : true,
            filter        : true,
            width         : 128
        } );

        columnDataDescriptor = buildDataDescriptor( { attributePath: 'reference.validatorUserId' }, dataStructure ) as ObjectDataDescriptor;
        retVal.push( {
            key           : columnDataDescriptor.alias,
            field         : columnDataDescriptor.alias,
            header        : 'YN.base.finder.validatedBy',
            dataDescriptor: columnDataDescriptor,
            permissions   : {},
            editor        : true,
            filter        : true,
            width         : 128
        } );
    }

    return retVal;
}
